import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import R from 'ramda';
import classNames from 'classnames';

import Button from './Button';
import Tooltip from './Tooltip';

export const HOVER_BUTTON_PROPS = [
  'componentShownOnHover',
  'textShownOnHover',
  'onHover',
  'activeChild',
  'position'
];

class HoverButton extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      isHovering: false
    };
  }

  _onHoverEnter = () => {
    this.props.onHover();
    this.setState({ isHovering: true }, () => {
      // in order to prevent the Mixpanel event from firing multiple
      // times with each mouse movement of the user, we set a timeout
      // and check they're still hovering before firing the event
      setTimeout(() => {
        if (this.state.isHovering) {
          const i13nModel = R.merge({ label: 'Button' }, this.props.i13nModel);
          // prepend with 'Hover', as there is no specific 'Hover' action
          i13nModel.label = `Hover ${i13nModel.label}`;

          this.context.i13n.executeEvent('log', i13nModel);
        }
      }, 200);
    });
  };

  _onHoverLeave = () => {
    this.setState({ isHovering: false });
  };

  render() {
    const propsToOmitFromButton = [
      ...HOVER_BUTTON_PROPS,
      'i13nModel',
      'containerClassName'
    ];
    const i13nModel = R.merge({ label: 'Hover button' }, this.props.i13nModel);
    const buttonProps = R.omit(propsToOmitFromButton, this.props);
    let buttonChild = this.props.children;
    if (this.props.activeChild && this.state.isHovering) {
      buttonChild = this.props.activeChild;
    }

    const button = (
      <Button i13nModel={i13nModel} {...buttonProps}>
        {buttonChild}
      </Button>
    );

    if (this.props.componentShownOnHover) {
      return (
        <div
          onMouseOver={this._onHoverEnter}
          onMouseOut={this._onHoverLeave}
          className={classNames(
            {
              'h_d-ib': !this.props.containerClassName
            },
            this.props.containerClassName
          )}
        >
          <Tooltip
            html={this.props.componentShownOnHover}
            position={this.props.position}
          >
            {button}
          </Tooltip>
        </div>
      );
    }

    return (
      <div onMouseOver={this._onHoverEnter} onMouseOut={this._onHoverLeave}>
        {button}
        <div
          className={classNames('hover-button__outer-container', {
            'h_d-n': !this.state.isHovering
          })}
        >
          <div className="hover-button__inner-container">
            {this.props.textShownOnHover}
          </div>
        </div>
      </div>
    );
  }
}

HoverButton.propTypes = {
  /**
   * If passed in, will be placed within core Tooltip component to show
   * on hover against a white background
   */
  componentShownOnHover: PropTypes.object,
  /**
   * If passed in, will be displayed below the button on hover against a dark
   * grey background
   */
  textShownOnHover: PropTypes.string,
  /**
   * Optional function to fire on hover (mixpanel event will
   * automatically be triggered regardless).
   */
  onHover: PropTypes.func,
  /**
   * Optional element to show on hover only. If not passed in,
   * OR not hovering, the child element will be shown instead.
   */
  activeChild: PropTypes.element,
  /**
   * Any properties passed on the i13nModel prop will overwrite the
   * corresponding default property triggered on hover/click. Label will
   * be prepended with 'Hover' or 'Click' in actual event title.
   */
  i13nModel: PropTypes.object,
  /**
   * Where to position the tooltip if componentShownOnHover is used
   */
  position: PropTypes.string
  /**
   * Any other props are passed directly down to the Button component
   */
};

HoverButton.defaultProps = {
  onHover: R.identity,
  testid: 'hover-button'
};

HoverButton.contextTypes = {
  i13n: PropTypes.object.isRequired
};

export default HoverButton;
